import { App, Empty, Flex, Skeleton } from "antd"
import React, { useEffect, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useParams } from "react-router"
import { useNavigate } from "react-router-dom"

import {
  DocumentReviewRejectModal,
  type DocumentReviewRejectModalMode
} from "../../../components/Documents/DocumentReviewRejectModal.tsx"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import type { RootState } from "../../../store.ts"
import {
  approveContract,
  approveContract as approveContractThunk,
  cancelContract,
  cancelContract as cancelContractThunk,
  duplicateContract,
  fetchContract,
  fetchContracts,
  rejectContract,
  rejectContract as rejectContractThunk
} from "../redux/contracts.thunks.ts"
import { ContractViewBody } from "./components/ContractViewBody.tsx"
import { ContractViewHeader } from "./components/ContractViewHeader.tsx"
import { useCallbackRef } from "../../../hooks/use-callback-ref.ts"

function ContractViewPage({
  contract,
  fetching,
  viewLeadAllowed,
  saving,
  getContracts,
  getContact,
  rejectContract,
  cancelContract,
  approveContract,
  duplicateContract
}: ConnectedProps<typeof connector>) {
  const navigate = useNavigate()
  const { modal } = App.useApp()
  const id = useParams()?.id as string
  const [rejectModalMode, setRejectModalMode] =
    useState<DocumentReviewRejectModalMode | null>(null)

  const getContactLatest = useCallbackRef(getContact)
  useEffect(() => {
    if (!id) return

    getContactLatest(id)
  }, [id, getContactLatest])

  useEffect(() => {
    if (!contract) return
    generateDocumentTitle(contract?.contract_id)
  }, [contract])

  if (fetching) {
    return <Skeleton.Node active className={"size-full"} />
  }

  if (!contract) {
    return (
      <Empty
        className={"mx-auto"}
        description={"The contract was not found."}
      />
    )
  }

  const handleRejectSubmit = async (reason: string) => {
    const action =
      rejectModalMode === "cancel"
        ? await cancelContract({
            id: contract.id,
            reason: reason.trim(),
            notes: ""
          })
        : await rejectContract({
            id: contract.id,
            reason: reason.trim()
          })

    const isSuccess =
      rejectModalMode === "cancel"
        ? cancelContractThunk.fulfilled.match(action)
        : rejectContractThunk.fulfilled.match(action)

    if (isSuccess) {
      setRejectModalMode(null)
      navigate(RoutesPaths.contract.list)
      getContracts()
    }
  }

  const handleApproveContract = async () => {
    const action = await approveContract(contract.id)
    if (approveContractThunk.fulfilled.match(action)) {
      navigate(RoutesPaths.contract.list)
      getContracts()
    }
  }

  const handleDuplicateContract = () => {
    modal.confirm({
      centered: true,
      title: "Confirm duplication",
      content: "Are you sure you want to duplicate this contract?",
      cancelText: "Close",
      okText: "Duplicate",
      onOk: async () => {
        const newContract = await duplicateContract(contract?.id)?.unwrap()

        if (!newContract) return

        const href = contract?.is_version_2
          ? RoutesPaths.contractDocument.edit(newContract?.id)
          : RoutesPaths.contract.edit(newContract?.id)

        navigate(href)
      }
    })
  }

  return (
    <Flex vertical gap={"small"} flex={1}>
      {rejectModalMode && (
        <DocumentReviewRejectModal
          mode={rejectModalMode}
          saving={saving}
          onSubmit={handleRejectSubmit}
          onClose={() => setRejectModalMode(null)}
        />
      )}

      <ContractViewHeader
        contract={contract}
        saving={saving}
        viewLeadAllowed={viewLeadAllowed}
        onApproveContract={handleApproveContract}
        onUnderRevision={() => setRejectModalMode("revision")}
        onCancelReview={() => setRejectModalMode("cancel")}
        onDuplicateContract={handleDuplicateContract}
      />
      <ContractViewBody contract={contract} />
    </Flex>
  )
}

const mapStateToProps = (state: RootState) => ({
  contract: state.contracts.contract,
  fetching: state.contracts.fetching,
  saving: state.contracts.saveFetching,
  viewLeadAllowed:
    state?.auth?.userInfo?.permissions?.codes?.leads?.leads?.lead_view
})

const mapDispatchToProps = {
  getContracts: fetchContracts,
  getContact: fetchContract,
  rejectContract,
  cancelContract,
  approveContract,
  duplicateContract
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ContractViewPage)
